import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, useOutletContext, Navigate } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, getDocs, where, limit, startAfter, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import QRCode from 'qrcode';
import { useToast } from '../components/ToastProvider';
import { LoadingState, ErrorState, EmptyState } from '../components/States';
import { filterByDateRange, exportToCsv, exportTableToPdf } from '../utils/export';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    BarElement,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, BarElement);

const ADMIN_UIDS = [
    'cXXmwFwOebRnN0h6rPb1E4ASRIt1',
    'WosYCVaIbrXGFLST7sjEQc1WQaU2',
];

const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            mode: 'index',
            intersect: false,
        },
    },
    scales: {
        y: {
            beginAtZero: true,
            ticks: { precision: 0 },
            grid: { color: 'rgba(148, 163, 184, 0.2)' },
        },
        x: {
            grid: { display: false },
        },
    },
};

const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
    },
    scales: {
        y: {
            beginAtZero: true,
            ticks: { precision: 0 },
            grid: { color: 'rgba(148, 163, 184, 0.2)' },
        },
        x: {
            grid: { display: false },
        },
    },
};

const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'bottom',
            labels: { boxWidth: 12 },
        },
    },
};

const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
);

const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);

const EyeOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
);

const CopyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
);

const ShareIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6.002l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
    </svg>
);

const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

const StatCard = ({ title, value, hint }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
        <p className="mt-1 text-2xl font-bold text-slate-800">{Number(value || 0).toLocaleString()}</p>
        {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
);

const Panel = ({ title, subtitle, children, rightContent }) => (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-3">
            <div>
                <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
                {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
            </div>
            {rightContent}
        </div>
        {children}
    </section>
);

const ShareButton = ({ shortUrl, longUrl, onCopy }) => {
    const handleShare = async () => {
        const shareData = {
            title: 'แชร์ลิงก์ย่อ',
            text: `ลิงก์สำหรับ: ${longUrl}`,
            url: shortUrl,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (_) {
                // ยกเลิกการแชร์ ไม่ต้องทำอะไร
            }
        } else {
            onCopy(shortUrl);
        }
    };

    return (
        <button
            onClick={handleShare}
            className="rounded-full p-1 text-slate-400 transition-colors hover:bg-green-100 hover:text-green-700"
            title="แชร์ลิงก์"
        >
            <ShareIcon />
        </button>
    );
};

const getDeviceType = (ua) => {
    const lowerUa = ua?.toLowerCase() || '';
    if (/mobile|android|iphone|ipad|ipod/.test(lowerUa)) return 'มือถือ';
    return 'เดสก์ท็อป';
};

const getBrowser = (ua) => {
    const lowerUa = ua?.toLowerCase() || '';
    if (lowerUa.includes('edg')) return 'Edge';
    if (lowerUa.includes('chrome')) return 'Chrome';
    if (lowerUa.includes('safari')) return 'Safari';
    if (lowerUa.includes('firefox')) return 'Firefox';
    return 'อื่น ๆ';
};

const getOs = (ua) => {
    const lowerUa = ua?.toLowerCase() || '';
    if (lowerUa.includes('windows')) return 'Windows';
    if (lowerUa.includes('mac os')) return 'macOS';
    if (lowerUa.includes('android')) return 'Android';
    if (lowerUa.includes('iphone') || lowerUa.includes('ipad')) return 'iOS';
    if (lowerUa.includes('linux')) return 'Linux';
    return 'อื่น ๆ';
};

const LinkDetails = () => {
    const { linkId } = useParams();
    const { user } = useOutletContext();
    const { showToast } = useToast();

    const [linkData, setLinkData] = useState(null);
    const [clicks, setClicks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(null);

    const [timeRange, setTimeRange] = useState('7days');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [qrSize, setQrSize] = useState(300);
    const [logo, setLogo] = useState(null);
    const [includeLogo, setIncludeLogo] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);

    const historyPageSize = 10;
    const [historyClicks, setHistoryClicks] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyHasMore, setHistoryHasMore] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyCursors, setHistoryCursors] = useState({ 1: null });
    const [historyLastVisibleDoc, setHistoryLastVisibleDoc] = useState(null);

    const canvasRef = useRef(null);

    const loadClicksPage = useCallback(
        async (pageNumber, cursorDoc = null) => {
            if (!linkId || isAuthorized !== true) return;

            setHistoryLoading(true);
            try {
                const constraints = [orderBy('timestamp', 'desc')];

                if (dateFrom) {
                    constraints.push(where('timestamp', '>=', Timestamp.fromDate(new Date(`${dateFrom}T00:00:00`))));
                }
                if (dateTo) {
                    constraints.push(where('timestamp', '<=', Timestamp.fromDate(new Date(`${dateTo}T23:59:59.999`))));
                }
                if (cursorDoc) {
                    constraints.push(startAfter(cursorDoc));
                }

                constraints.push(limit(historyPageSize + 1));

                const pageQuery = query(collection(db, 'urls', linkId, 'clicks'), ...constraints);
                const snapshot = await getDocs(pageQuery);
                const docs = snapshot.docs;
                const hasNext = docs.length > historyPageSize;
                const pageDocs = hasNext ? docs.slice(0, historyPageSize) : docs;
                const nextCursorDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;

                setHistoryClicks(pageDocs.map((clickDoc) => ({ id: clickDoc.id, ...clickDoc.data() })));
                setHistoryHasMore(hasNext);
                setHistoryPage(pageNumber);
                setHistoryLastVisibleDoc(nextCursorDoc);

                setHistoryCursors((prev) => {
                    const next = { ...prev, [pageNumber]: cursorDoc || null };
                    if (hasNext && nextCursorDoc) {
                        next[pageNumber + 1] = nextCursorDoc;
                    }
                    return next;
                });
            } catch (err) {
                console.error('Error fetching paginated clicks:', err);
                showToast('ไม่สามารถโหลดประวัติการคลิกได้', 'error');
            } finally {
                setHistoryLoading(false);
            }
        },
        [linkId, isAuthorized, dateFrom, dateTo, historyPageSize, showToast]
    );

    useEffect(() => {
        if (!user || !user.uid) {
            setLoading(true);
            return;
        }

        const fetchDetails = async () => {
            setLoading(true);
            setError(null);
            setIsAuthorized(null);

            try {
                const linkDocRef = doc(db, 'urls', linkId);
                const linkDocSnap = await getDoc(linkDocRef);

                if (!linkDocSnap.exists()) {
                    setError('ไม่พบข้อมูลลิงก์');
                    setIsAuthorized(false);
                    return;
                }

                const data = { id: linkDocSnap.id, ...linkDocSnap.data() };
                const isAdmin = ADMIN_UIDS.includes(user.uid);
                const isOwner = data.uid === user.uid;

                if (!isAdmin && !isOwner) {
                    setIsAuthorized(false);
                    setError('คุณไม่มีสิทธิ์เข้าถึงข้อมูลนี้');
                    return;
                }

                setIsAuthorized(true);
                setLinkData(data);

                const clicksQuery = query(collection(db, 'urls', linkId, 'clicks'), orderBy('timestamp', 'desc'));
                const clicksSnapshot = await getDocs(clicksQuery);
                setClicks(clicksSnapshot.docs.map((clickDoc) => ({ id: clickDoc.id, ...clickDoc.data() })));
                setHistoryCursors({ 1: null });
                setHistoryLastVisibleDoc(null);
            } catch (err) {
                console.error('Error fetching link details:', err);
                setError('เกิดข้อผิดพลาดในการดึงข้อมูล');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [linkId, user]);

    useEffect(() => {
        if (loading || isAuthorized !== true || !canvasRef.current) return;

        const generateQRCode = async () => {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            const baseUrl = window.location.origin;

            const isPlaceholder = !linkData;
            const urlToEncode = isPlaceholder ? baseUrl : `${baseUrl}/${linkData.shortCode}`;
            const qrColor = isPlaceholder ? '#E5E7EB' : '#111827';

            const options = {
                errorCorrectionLevel: 'H',
                margin: 2,
                width: qrSize,
                color: { dark: qrColor, light: '#FFFFFF' },
            };

            try {
                const qrCodeDataUrl = await QRCode.toDataURL(urlToEncode, options);
                const qrImage = new Image();
                qrImage.src = qrCodeDataUrl;
                qrImage.onload = () => {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(qrImage, 0, 0, qrSize, qrSize);

                    if (!isPlaceholder && logo && includeLogo) {
                        const logoImg = new Image();
                        logoImg.src = URL.createObjectURL(logo);
                        logoImg.onload = () => {
                            const logoSize = qrSize * 0.24;
                            const logoX = (qrSize - logoSize) / 2;
                            const logoY = (qrSize - logoSize) / 2;
                            ctx.fillStyle = '#FFFFFF';
                            ctx.fillRect(logoX - 6, logoY - 6, logoSize + 12, logoSize + 12);
                            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
                        };
                    }
                };
            } catch (err) {
                console.error('Failed to generate QR Code', err);
            }
        };

        generateQRCode();
    }, [linkData, qrSize, logo, includeLogo, loading, isAuthorized]);

    const handleDownloadQR = () => {
        if (!canvasRef.current || !linkData) return;
        const imageUrl = canvasRef.current.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = imageUrl;
        a.download = `qr-code-${linkData.shortCode}.png`;
        a.click();
    };

    const handleCopy = (textToCopy) => {
        if (!textToCopy) return;
        navigator.clipboard.writeText(textToCopy).then(
            () => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            },
            (err) => {
                console.error('Failed to copy text: ', err);
                showToast('ไม่สามารถคัดลอกลิงก์ได้', 'error');
            }
        );
    };

    const filteredClicks = useMemo(
        () => filterByDateRange(clicks, (click) => click.timestamp, dateFrom, dateTo),
        [clicks, dateFrom, dateTo]
    );

    useEffect(() => {
        if (isAuthorized === true && linkData) {
            setHistoryCursors({ 1: null });
            setHistoryLastVisibleDoc(null);
            loadClicksPage(1, null);
        }
    }, [dateFrom, dateTo, isAuthorized, linkData, loadClicksPage]);

    const processChartData = () => {
        const now = new Date();
        let startDate;

        if (timeRange === '7days') {
            startDate = new Date(new Date().setDate(now.getDate() - 7));
        } else if (timeRange === '30days') {
            startDate = new Date(new Date().setMonth(now.getMonth() - 1));
        } else {
            startDate = new Date(new Date().setFullYear(now.getFullYear() - 1));
        }

        const timeRangeFilteredClicks = filteredClicks.filter((click) => click.timestamp && click.timestamp.toDate() >= startDate);
        const clicksByDay = timeRangeFilteredClicks.reduce((acc, click) => {
            const day = click.timestamp.toDate().toLocaleDateString('en-CA');
            acc[day] = (acc[day] || 0) + 1;
            return acc;
        }, {});

        const sortedLabels = Object.keys(clicksByDay).sort();
        const data = sortedLabels.map((label) => clicksByDay[label]);
        const displayLabels = sortedLabels.map((label) =>
            new Date(label).toLocaleDateString('th-TH', { month: 'short', day: 'numeric' })
        );

        return {
            labels: displayLabels,
            datasets: [
                {
                    label: 'จำนวนคลิก',
                    data,
                    borderColor: '#2563EB',
                    backgroundColor: 'rgba(37, 99, 235, 0.16)',
                    pointBackgroundColor: '#1D4ED8',
                    pointRadius: 3,
                    borderWidth: 2,
                    fill: true,
                    tension: 0.3,
                },
            ],
        };
    };

    const processDeviceData = () => {
        const deviceTypes = filteredClicks.reduce((acc, click) => {
            const type = getDeviceType(click.userAgent);
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});

        return {
            labels: Object.keys(deviceTypes),
            datasets: [
                {
                    data: Object.values(deviceTypes),
                    backgroundColor: ['#3B82F6', '#10B981', '#F59E0B'],
                    hoverBackgroundColor: ['#2563EB', '#059669', '#D97706'],
                    borderWidth: 0,
                },
            ],
        };
    };

    const processBrowserData = () => {
        const browsers = filteredClicks.reduce((acc, click) => {
            const browser = getBrowser(click.userAgent);
            acc[browser] = (acc[browser] || 0) + 1;
            return acc;
        }, {});

        return {
            labels: Object.keys(browsers),
            datasets: [
                {
                    data: Object.values(browsers),
                    backgroundColor: ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#8B5CF6'],
                    hoverBackgroundColor: ['#DC2626', '#D97706', '#059669', '#2563EB', '#4F46E5', '#7C3AED'],
                    borderWidth: 0,
                },
            ],
        };
    };

    const processOsData = () => {
        const oses = filteredClicks.reduce((acc, click) => {
            const os = getOs(click.userAgent);
            acc[os] = (acc[os] || 0) + 1;
            return acc;
        }, {});

        return {
            labels: Object.keys(oses),
            datasets: [
                {
                    data: Object.values(oses),
                    backgroundColor: ['#22C55E', '#0EA5E9', '#8B5CF6', '#F43F5E', '#6B7280', '#EC4899'],
                    hoverBackgroundColor: ['#16A34A', '#0284C7', '#7C3AED', '#E11D48', '#4B5563', '#DB2777'],
                    borderWidth: 0,
                },
            ],
        };
    };

    const processTimeOfDayData = () => {
        const clicksByHour = Array(24).fill(0);
        filteredClicks.forEach((click) => {
            if (click.timestamp) {
                const hour = click.timestamp.toDate().getHours();
                clicksByHour[hour] += 1;
            }
        });

        const labels = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
        return {
            labels,
            datasets: [
                {
                    label: 'จำนวนคลิกรายชั่วโมง',
                    data: clicksByHour,
                    backgroundColor: 'rgba(22, 163, 74, 0.18)',
                    borderColor: 'rgb(22, 163, 74)',
                    borderWidth: 2,
                    fill: true,
                },
            ],
        };
    };

    const getReferrers = () => {
        const grouped = filteredClicks.reduce((acc, click) => {
            let referrer = 'Direct';
            try {
                if (click.referrer && click.referrer !== 'Direct' && click.referrer !== '') {
                    referrer = new URL(click.referrer).hostname.replace('www.', '');
                }
            } catch (_) {
                referrer = 'Direct';
            }
            acc[referrer] = (acc[referrer] || 0) + 1;
            return acc;
        }, {});

        return Object.entries(grouped).sort(([, a], [, b]) => b - a);
    };

    const referrers = getReferrers();
    const baseUrl = window.location.origin;
    const shortUrl = linkData ? `${baseUrl}/${linkData.shortCode}` : '';

    const sourceFilteredClicks = sourceFilter
        ? filteredClicks.filter((click) => (click.referrer || 'Direct').toLowerCase().includes(sourceFilter.toLowerCase()))
        : filteredClicks;
    const sourceFilteredHistoryClicks = sourceFilter
        ? historyClicks.filter((click) => (click.referrer || 'Direct').toLowerCase().includes(sourceFilter.toLowerCase()))
        : historyClicks;

    const currentClicks = sourceFilteredHistoryClicks;

    const handleNextHistoryPage = () => {
        if (!historyHasMore || historyLoading) return;
        const nextPage = historyPage + 1;
        const cursor = historyCursors[nextPage] || historyLastVisibleDoc || null;
        loadClicksPage(nextPage, cursor);
    };

    const handlePrevHistoryPage = () => {
        if (historyPage <= 1 || historyLoading) return;
        const prevPage = historyPage - 1;
        const cursor = historyCursors[prevPage] || null;
        loadClicksPage(prevPage, cursor);
    };

    const knownHistoryPages = Object.keys(historyCursors)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
    if (!knownHistoryPages.includes(historyPage)) knownHistoryPages.push(historyPage);
    if (historyHasMore && !knownHistoryPages.includes(historyPage + 1)) knownHistoryPages.push(historyPage + 1);
    const visibleHistoryPages = Array.from(new Set(knownHistoryPages))
        .sort((a, b) => a - b)
        .filter((value) => value >= Math.max(1, historyPage - 3) && value <= historyPage + 3);

    const handleGotoHistoryPage = (targetPage) => {
        if (targetPage === historyPage || historyLoading) return;
        if (targetPage === historyPage + 1) {
            handleNextHistoryPage();
            return;
        }
        if (targetPage === historyPage - 1) {
            handlePrevHistoryPage();
            return;
        }
        if (!(targetPage in historyCursors)) return;
        loadClicksPage(targetPage, historyCursors[targetPage] || null);
    };

    const handleExportCsv = () => {
        exportToCsv(
            `link-details-${linkData?.shortCode || 'report'}.csv`,
            ['วันเวลา', 'ประเภทอุปกรณ์', 'แหล่งที่มา', 'User Agent'],
            sourceFilteredClicks.map((click) => [
                click.timestamp ? new Date(click.timestamp.toDate()).toLocaleString('th-TH') : '-',
                getDeviceType(click.userAgent),
                click.referrer || 'Direct',
                click.userAgent || '-',
            ])
        );
        showToast('ส่งออก CSV สำเร็จ', 'success');
    };

    const handleExportPdf = () => {
        const ok = exportTableToPdf({
            filename: `link-details-${linkData?.shortCode || 'report'}.pdf`,
            title: `\u0e23\u0e32\u0e22\u0e07\u0e32\u0e19\u0e25\u0e34\u0e07\u0e01\u0e4c ${linkData?.shortCode || ''}`,
            subtitle: `\u0e08\u0e33\u0e19\u0e27\u0e19\u0e1b\u0e23\u0e30\u0e27\u0e31\u0e15\u0e34\u0e04\u0e25\u0e34\u0e01 ${sourceFilteredClicks.length} \u0e23\u0e32\u0e22\u0e01\u0e32\u0e23`,
            columns: ['\u0e27\u0e31\u0e19\u0e40\u0e27\u0e25\u0e32', '\u0e1b\u0e23\u0e30\u0e40\u0e20\u0e17\u0e2d\u0e38\u0e1b\u0e01\u0e23\u0e13\u0e4c', '\u0e41\u0e2b\u0e25\u0e48\u0e07\u0e17\u0e35\u0e48\u0e21\u0e32', 'User Agent'],
            rows: sourceFilteredClicks.map((click) => [
                click.timestamp ? new Date(click.timestamp.toDate()).toLocaleString('th-TH') : '-',
                getDeviceType(click.userAgent),
                click.referrer || 'Direct',
                click.userAgent || '-',
            ]),
        });
        if (ok) showToast('\u0e2a\u0e48\u0e07\u0e2d\u0e2d\u0e01 PDF \u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08', 'success');
        else showToast('\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e2a\u0e48\u0e07\u0e2d\u0e2d\u0e01 PDF \u0e44\u0e14\u0e49', 'error');
    };

    if (loading || isAuthorized === null) {
        return <LoadingState message="กำลังโหลด..." />;
    }

    if (isAuthorized === false) {
        return <Navigate to="/" replace />;
    }

    if (error) {
        return <ErrorState message={error} />;
    }

    return (
        <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50/40">
            <div className="container mx-auto p-4 sm:p-6 lg:p-8">
                <div className="mb-5">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path
                                fillRule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clipRule="evenodd"
                            />
                        </svg>
                        กลับไปหน้าหลัก
                    </Link>
                </div>

                <section className="mb-6 rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-sm backdrop-blur sm:p-8">
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-800 sm:text-4xl">รายละเอียดลิงก์และสถิติ</h1>
                    <p className="mt-2 text-sm text-slate-600 sm:text-base">
                        ดูข้อมูลลิงก์, วิเคราะห์พฤติกรรมการคลิก และจัดการ QR Code ได้ในหน้าเดียว
                    </p>

                    <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        <StatCard title="จำนวนคลิกทั้งหมด" value={linkData?.clickCount || 0} hint="ยอดสะสมของลิงก์นี้" />
                        <StatCard title="จำนวนเหตุการณ์คลิก" value={filteredClicks.length} hint="เรคคอร์ดคลิกที่เก็บในระบบ" />
                        <StatCard title="ผู้เข้าชมจากแหล่งที่มา" value={referrers.length} hint="จำนวนโดเมนอ้างอิงที่แตกต่าง" />
                        <StatCard title="สถานะการป้องกัน" value={linkData?.password ? 1 : 0} hint={linkData?.password ? 'มีรหัสผ่าน' : 'ไม่มีรหัสผ่าน'} />
                    </div>
                </section>

                <section className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
                    <div className="space-y-6 xl:col-span-8">
                        <Panel title="ข้อมูลลิงก์" subtitle="รายละเอียดลิงก์ต้นฉบับ ลิงก์ย่อ และการตั้งค่าที่เกี่ยวข้อง">
                            <div className="space-y-5 text-sm">
                                <div>
                                    <p className="mb-1 font-semibold text-slate-500">ลิงก์ต้นฉบับ</p>
                                    <a
                                        href={linkData.longUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="break-all text-blue-700 hover:underline"
                                    >
                                        {linkData.longUrl}
                                    </a>
                                </div>

                                <div>
                                    <p className="mb-1 font-semibold text-slate-500">ลิงก์ย่อ</p>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                                            {shortUrl.replace(/^https?:\/\//, '')}
                                        </a>
                                        <button
                                            onClick={() => handleCopy(shortUrl)}
                                            className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                                            title="คัดลอกลิงก์"
                                        >
                                            <CopyIcon />
                                        </button>
                                        <ShareButton shortUrl={shortUrl} longUrl={linkData.longUrl} onCopy={handleCopy} />
                                        {copySuccess ? <span className="text-xs font-semibold text-green-600">คัดลอกแล้ว</span> : null}
                                    </div>
                                </div>

                                {linkData.tags && linkData.tags.length > 0 ? (
                                    <div>
                                        <p className="mb-2 font-semibold text-slate-500">ป้ายกำกับ</p>
                                        <div className="flex flex-wrap gap-2">
                                            {linkData.tags.map((tag) => (
                                                <span key={tag} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}

                                {linkData.password ? (
                                    <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                                        <p className="font-semibold text-slate-500">รหัสผ่าน</p>
                                        {isPasswordVisible ? (
                                            <span className="rounded bg-slate-200 px-2 py-1 font-mono text-slate-800">{linkData.password}</span>
                                        ) : (
                                            <span className="rounded bg-slate-200 px-2 py-1 font-mono text-slate-800">••••••••</span>
                                        )}
                                        <button
                                            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                            className="p-1 text-slate-500 hover:text-slate-800"
                                            title={isPasswordVisible ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                                        >
                                            {isPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </Panel>

                        <Panel
                            title="สถิติการคลิกรายวัน"
                            subtitle="ดูแนวโน้มการคลิกตามช่วงเวลา"
                            rightContent={
                                <div className="flex flex-wrap items-center justify-end gap-2">
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
                                    />
                                    <input
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
                                    />
                                    <select
                                        value={timeRange}
                                        onChange={(e) => setTimeRange(e.target.value)}
                                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm transition focus:border-blue-500 focus:ring-blue-500"
                                    >
                                        <option value="7days">7 วันล่าสุด</option>
                                        <option value="30days">30 วันล่าสุด</option>
                                        <option value="1year">1 ปีล่าสุด</option>
                                    </select>
                                </div>
                            }
                        >
                            <div className="h-[280px] sm:h-[320px]">
                                <Line data={processChartData()} options={lineChartOptions} />
                            </div>
                        </Panel>
                    </div>

                    <div className="space-y-6 xl:col-span-4">
                        <Panel title="QR Code" subtitle="ดาวน์โหลดและปรับแต่ง QR Code สำหรับลิงก์นี้">
                            <div className="text-center">
                                <canvas ref={canvasRef} width={qrSize} height={qrSize} className="mx-auto rounded-xl border border-slate-200 bg-white p-1" />
                                <div className="mt-4 space-y-4">
                                    <div className="flex items-center justify-center">
                                        <input
                                            id="include-logo"
                                            type="checkbox"
                                            checked={includeLogo}
                                            onChange={(e) => {
                                                setIncludeLogo(e.target.checked);
                                                if (!e.target.checked) setLogo(null);
                                            }}
                                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            disabled={!linkData}
                                        />
                                        <label htmlFor="include-logo" className="ml-2 block text-sm font-medium text-slate-700">
                                            ใส่โลโก้ตรงกลาง
                                        </label>
                                    </div>

                                    {includeLogo ? (
                                        <div>
                                            <label htmlFor="logo-upload" className="sr-only">
                                                อัปโหลดโลโก้
                                            </label>
                                            <input
                                                id="logo-upload"
                                                type="file"
                                                accept="image/png, image/jpeg"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        setLogo(e.target.files[0]);
                                                    }
                                                }}
                                                className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-600 hover:file:bg-blue-100"
                                            />
                                        </div>
                                    ) : null}

                                    <div className="flex items-center justify-center gap-2">
                                        <label htmlFor="qr-size" className="text-sm font-medium text-slate-700">
                                            ขนาด:
                                        </label>
                                        <select
                                            id="qr-size"
                                            value={qrSize}
                                            onChange={(e) => setQrSize(Number(e.target.value))}
                                            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm transition focus:border-blue-500 focus:ring-blue-500"
                                        >
                                            <option value="150">เล็ก</option>
                                            <option value="300">กลาง</option>
                                            <option value="500">ใหญ่</option>
                                        </select>
                                    </div>

                                    <button
                                        onClick={handleDownloadQR}
                                        className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                                        disabled={!linkData}
                                    >
                                        <DownloadIcon />
                                        ดาวน์โหลด QR Code
                                    </button>
                                </div>
                            </div>
                        </Panel>
                    </div>
                </section>

                <section className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
                    <div className="space-y-6 xl:col-span-8">
                        <Panel title="ช่วงเวลาที่มีการคลิก (รายชั่วโมง)" subtitle="ช่วยดูพฤติกรรมการเข้าชมในแต่ละช่วงเวลา">
                            <div className="h-[280px] sm:h-[320px]">
                                <Bar data={processTimeOfDayData()} options={barChartOptions} />
                            </div>
                        </Panel>
                    </div>

                    <div className="space-y-6 xl:col-span-4">
                        <Panel title="แหล่งที่มาของทราฟฟิก" subtitle="เรียงจากจำนวนคลิกมากไปน้อย">
                            {referrers.length > 0 ? (
                                <ul className="max-h-[320px] space-y-1 overflow-y-auto pr-1 text-sm">
                                    {referrers.map(([referrer, count]) => (
                                        <li key={referrer} className="flex items-center justify-between rounded-md px-2 py-1.5 even:bg-slate-50">
                                            <span className="max-w-[220px] truncate text-slate-600" title={referrer}>
                                                {referrer}
                                            </span>
                                            <span className="font-semibold text-slate-800">{count.toLocaleString()}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-slate-500">ยังไม่มีข้อมูลแหล่งที่มา</p>
                            )}
                        </Panel>
                    </div>
                </section>

                <section className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <Panel title="ประเภทอุปกรณ์">
                        <div className="h-[260px]">
                            <Doughnut data={processDeviceData()} options={donutOptions} />
                        </div>
                    </Panel>

                    <Panel title="เบราว์เซอร์">
                        <div className="h-[260px]">
                            <Doughnut data={processBrowserData()} options={donutOptions} />
                        </div>
                    </Panel>

                    <Panel title="ระบบปฏิบัติการ">
                        <div className="h-[260px]">
                            <Doughnut data={processOsData()} options={donutOptions} />
                        </div>
                    </Panel>
                </section>

                <Panel title="ประวัติการคลิกล่าสุด" subtitle="รายละเอียดการคลิกในแต่ละครั้ง">
                    {sourceFilteredHistoryClicks.length > 0 ? (
                        <>
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                <input
                                    type="text"
                                    value={sourceFilter}
                                    onChange={(e) => {
                                        setSourceFilter(e.target.value);
                                    }}
                                    placeholder="ค้นหาแหล่งที่มา..."
                                    className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                />
                                <div className="flex gap-2">
                                    <button type="button" onClick={handleExportCsv} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium">
                                        ส่งออก CSV
                                    </button>
                                    <button type="button" onClick={handleExportPdf} className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white">
                                        ส่งออก PDF
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[860px] text-left text-sm">
                                    <thead className="border-b border-slate-200 bg-slate-50">
                                        <tr>
                                            <th className="p-3 font-semibold text-slate-600">วัน-เวลา</th>
                                            <th className="p-3 font-semibold text-slate-600">ประเภทอุปกรณ์</th>
                                            <th className="p-3 font-semibold text-slate-600">แหล่งที่มา</th>
                                            <th className="p-3 font-semibold text-slate-600">User Agent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentClicks.map((click) => (
                                            <tr key={click.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                                                <td className="whitespace-nowrap p-3">
                                                    {click.timestamp ? new Date(click.timestamp.toDate()).toLocaleString('th-TH') : '-'}
                                                </td>
                                                <td className="p-3">{getDeviceType(click.userAgent)}</td>
                                                <td className="max-w-[220px] truncate p-3" title={click.referrer || 'Direct'}>
                                                    {click.referrer || 'Direct'}
                                                </td>
                                                <td className="max-w-[360px] truncate p-3 text-slate-500" title={click.userAgent}>
                                                    {click.userAgent || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {(historyPage > 1 || historyHasMore) ? (
                                <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="text-sm text-slate-600">
                                        หน้า {historyPage}
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handlePrevHistoryPage}
                                            disabled={historyPage === 1 || historyLoading}
                                            className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <ChevronLeftIcon />
                                            ก่อนหน้า
                                        </button>

                                        <div className="flex items-center gap-1 overflow-x-auto">
                                            {visibleHistoryPages.map((pageNumber) => (
                                                <button
                                                    key={pageNumber}
                                                    type="button"
                                                    onClick={() => handleGotoHistoryPage(pageNumber)}
                                                    disabled={
                                                        historyLoading ||
                                                        pageNumber === historyPage ||
                                                        (!(pageNumber in historyCursors) && pageNumber !== historyPage + 1)
                                                    }
                                                    className={`min-w-9 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                                                        pageNumber === historyPage
                                                            ? 'border-blue-600 bg-blue-600 text-white'
                                                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50'
                                                    }`}
                                                >
                                                    {pageNumber}
                                                </button>
                                            ))}
                                        </div>

                                        <button
                                            onClick={handleNextHistoryPage}
                                            disabled={!historyHasMore || historyLoading}
                                            className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            ถัดไป
                                            <ChevronRightIcon />
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <EmptyState title="ยังไม่มีประวัติการคลิกสำหรับลิงก์นี้" />
                    )}
                </Panel>
            </div>
        </main>
    );
};

export default LinkDetails;
